// Schémas dans les leçons — un visuel structuré (figure géométrique, graphe de
// fonction, tableau…, même format que les `visual_blocks` des exercices et le
// recto des SKILLCARDS) est inséré dans le markdown d'une leçon sous la forme
// d'un bloc de code clôturé :
//
//   ```jr-visual
//   {"type":"constrained_geometry", …}
//   ```
//
// Il traverse donc tel quel `lessons.content_md` (aplati depuis les blocs V2)
// et est rendu en figure par `LessonMarkdown` (@jeraisonne/ui). Pur et testé.

export const LESSON_VISUAL_FENCE = 'jr-visual';

export type LessonMarkdownPart =
  | { kind: 'markdown'; md: string }
  | { kind: 'visual'; block: Record<string, unknown> & { type: string } }
  | { kind: 'invalid_visual'; raw: string };

const FENCE_RE = /^```jr-visual[ \t]*\n([\s\S]*?)\n```[ \t]*$/gm;

/** Découpe un markdown de leçon en texte / schémas (JSON invalide signalé, jamais d'exception). */
export function splitLessonMarkdown(md: string): LessonMarkdownPart[] {
  const parts: LessonMarkdownPart[] = [];
  let last = 0;
  for (const m of md.matchAll(FENCE_RE)) {
    const before = md.slice(last, m.index).trim();
    if (before) parts.push({ kind: 'markdown', md: before });
    const raw = m[1] ?? '';
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (
        parsed &&
        typeof parsed === 'object' &&
        typeof (parsed as { type?: unknown }).type === 'string'
      ) {
        parts.push({ kind: 'visual', block: parsed as Record<string, unknown> & { type: string } });
      } else {
        parts.push({ kind: 'invalid_visual', raw });
      }
    } catch {
      parts.push({ kind: 'invalid_visual', raw });
    }
    last = (m.index ?? 0) + m[0].length;
  }
  const rest = md.slice(last).trim();
  if (rest) parts.push({ kind: 'markdown', md: rest });
  return parts;
}

/** Bloc clôturé à insérer dans un texte de leçon pour un visuel structuré. */
export function lessonVisualFence(block: { type: string } & Record<string, unknown>): string {
  return `\`\`\`${LESSON_VISUAL_FENCE}\n${JSON.stringify(block)}\n\`\`\``;
}

/** La leçon contient-elle au moins un schéma ? */
export function hasLessonVisual(md: string): boolean {
  return splitLessonMarkdown(md).some((p) => p.kind === 'visual');
}
